<template>
  <Popup
    v-model:visible="currentVisible"
    :visible.sync="currentVisible"
    :class="classes(n('wrapper'))"
    position="bottom"
    :close-on-click-overlay="false"
    :lock-scroll="lockScroll"
    :overlay-class="overlayClass"
    :destroy-on-close="destroyOnClose"
    :teleport="teleport"
    :duration="0.3"
    :overlay="true"
    @open="handleOpen"
    @opened="handleOpened"
    @closed="handleClosed"
    @clickOverlay="handleOverlayClick"
    @close="handleCloseEvent"
  >
    <div
      v-if="shouldRender"
      :class="classes(n())"
      :style="dialogStyle"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="contentId"
    >
      <!-- 标题区域 -->
      <div
        :id="titleId"
        :class="classes(
          n('header'),
          {
            [n('header--with-subtitle')]: subTitle && !$slots.header && !$slots['header-center'],
            [n('header--with-title')]: hasTitle,
            [n('header--empty')]: isEmptyHeader
          }
        )"
      >
        <!-- 方式1：使用整体 header slot（优先级最高） -->
        <slot
          v-if="$slots.header"
          name="header"
        />

        <!-- 方式2：使用分区域 slot -->
        <template v-else>
          <!-- 左上角区域 -->
          <div
            v-if="hasHeaderLeft || hasHeaderRight"
            :class="classes(
              n('header-left'),
              {
                [n('header-left--paired')]: hasHeaderRight && !hasHeaderLeft
              }
            )"
          >
            <slot name="header-left">
              <!-- 默认：关闭按钮（如果 showClose=true） -->
              <div
                v-if="showClose"
                :class="n('close')"
                role="button"
                tabindex="0"
                :aria-label="closeAriaLabel"
                @click="handleCloseClick"
              >
                <CloseIcon
                  width="48"
                  height="48"
                  color="#000000E6"
                />
              </div>
            </slot>
          </div>

          <!-- 中间区域 -->
          <div :class="n('header-center')">
            <slot name="header-center">
              <!-- 默认：标题和副标题（垂直布局） -->
              <div
                v-if="title || subTitle"
                :class="n('header-title')"
              >
                <span
                  v-if="title"
                  :class="n('header-title-main')"
                >
                  {{ title }}
                </span>
                <span
                  v-if="subTitle"
                  :class="n('header-title-sub')"
                >
                  {{ subTitle }}
                </span>
              </div>
            </slot>
          </div>

          <!-- 右上角区域 -->
          <div
            v-if="hasHeaderLeft || hasHeaderRight"
            :class="classes(
              n('header-right'),
              {
                [n('header-right--paired')]: hasHeaderLeft
              }
            )"
          >
            <slot name="header-right" />
          </div>
        </template>
      </div>

      <!-- 内容区：根据 scrollable 控制是否可滚动 -->
      <!-- Liteapp 环境使用 scroll-view -->
      <scroll-view
        v-if="isLiteapp"
        :id="contentId"
        ref="bodyRef"
        :class="classes(
          n('body'),
          {
            [n('body--with-gradient')]: shouldShowGradient,
            [n('body--no-scroll')]: !scrollable
          }
        )"
        :scroll-y="scrollable"
        :scroll-config="scrollable ? 'default' : 'disable'"
        :show-scrollbar="false"
        @scroll="handleScrollViewScroll"
      >
        <slot />
      </scroll-view>

      <!-- H5 环境使用原生 div -->
      <div
        v-else
        :id="contentId"
        ref="bodyRef"
        :class="classes(
          n('body'),
          {
            [n('body--with-gradient')]: shouldShowGradient,
            [n('body--no-scroll')]: !scrollable
          }
        )"
        @scroll="handleScroll"
      >
        <slot />
      </div>

      <!-- 底部操作区：固定不滚动 -->
      <div
        v-if="$slots.footer || actions.length > 0"
        :class="classes(
          n('footer'),
          { [n('footer--with-gradient')]: shouldShowGradient }
        )"
      >
        <!-- 方式1：使用 actions 配置（组件自动渲染） -->
        <template v-if="!$slots.footer && actions.length > 0">
          <div
            :class="classes(
              n('actions'),
              {
                [n('actions--horizontal')]: actionsLayout === 'horizontal' && actions.length === 2,
                [n('actions--vertical')]: actionsLayout === 'vertical' && actions.length === 2,
                [n('actions--single')]: actions.length === 1
              }
            )"
          >
            <Button
              v-for="(action, index) in actions"
              :key="action.key"
              :type="action.type || 'primary'"
              :theme="action.theme || 'default'"
              :disabled="action.disabled"
              size="large"
              :aria-label="action.text"
              @click="handleActionClick(action, index)"
            >
              {{ action.text }}
            </Button>
          </div>
        </template>

        <!-- 方式2：使用 footer slot（完全自定义，优先级最高） -->
        <slot name="footer" />
      </div>
    </div>
  </Popup>
</template>

<script lang="ts">
import {
  ref,
  computed,
  defineComponent,
  watch,
  nextTick,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import Popup from '../popup/popup.vue';
import Button from '../button/button.vue';
import { CloseIcon } from '@tencent/wphk-icon-vue';
import { platform } from '@tencent/wphk-platform-adapter';
import type { HalfScreenDialogAction } from './half-screen-dialog';

const { name, n, classes } = createNamespace('half-screen-dialog');

export default defineComponent({
  name,
  components: {
    Popup,
    Button,
    CloseIcon,
  },
  props,
  emits,
  setup(props, { emit, slots }) {
    // 生成唯一 ID 用于无障碍
    const titleId = `half-screen-dialog-title-${Date.now()}`;
    const contentId = `half-screen-dialog-content-${Date.now()}`;

    // 内部 visible 状态，用于控制 Popup
    const currentVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => {
        emit('update:visible', value);
      },
    });

    // 懒加载渲染状态
    const isRendered = ref(false);

    // ==================== Liteapp 环境适配 ====================
    // Liteapp 和小程序环境不支持 overflow: scroll，需要使用 scroll-view 组件
    const isLiteapp = platform.isLiteapp();

    // 内容区引用
    const bodyRef = ref<HTMLElement | null>(null);

    // 滚动状态
    const isScrolledToBottom = ref(false);

    // 是否有可滚动内容
    const hasScrollableContent = ref(false);

    // 计算是否有标题（用于设置不同的 padding-bottom）
    const hasTitle = computed(() => {
      // 如果使用了整体 header slot，假设有标题
      if (slots.header) {
        return true;
      }
      // 如果使用了 header-center slot，认为有标题
      if (slots['header-center']) {
        return true;
      }
      // 如果有 title 或 subTitle，认为有标题
      return !!(props.title || props.subTitle);
    });

    // 计算 header-left 是否有内容
    const hasHeaderLeft = computed(() => {
      // 如果使用了整体 header slot，不适用此逻辑
      if (slots.header) {
        return false;
      }
      // 如果有 header-left slot 内容或 showClose 为 true，则认为有内容
      return !!(slots['header-left'] || props.showClose);
    });

    // 计算 header-right 是否有内容
    const hasHeaderRight = computed(() => {
      // 如果使用了整体 header slot，不适用此逻辑
      if (slots.header) {
        return false;
      }
      // 如果有 header-right slot 内容，则认为有内容
      return !!slots['header-right'];
    });

    // 计算整个顶部区域是否为空
    const isEmptyHeader = computed(() => {
      // 如果使用了整体 header slot，不为空
      if (slots.header) {
        return false;
      }
      // 检查 header-left：如果有 slot 内容或 showClose 为 true，不为空
      if (slots['header-left'] || props.showClose) {
        return false;
      }
      // 检查 header-center：如果有 slot 内容或有 title/subTitle，不为空
      if (slots['header-center'] || props.title || props.subTitle) {
        return false;
      }
      // 检查 header-right：如果有 slot 内容，不为空
      if (slots['header-right']) {
        return false;
      }
      // 所有区域都为空
      return true;
    });

    // 计算是否应该渲染
    const shouldRender = computed(() => {
      // 如果不启用懒加载，始终渲染
      if (!props.lazyRender) return true;

      // 如果已经渲染过，保持渲染状态
      if (isRendered.value) return true;

      // 如果当前可见，标记为已渲染
      if (props.visible) {
        isRendered.value = true;
        return true;
      }

      return false;
    });

    // 获取顶部间距的 @unit 倍数（从 CSS 变量中读取，与 var.less 保持一致）
    const getTopSpacingUnitMultiplier = (): number => {
      // 顶部间距的 @unit 倍数默认值（与 var.less 中的 @half-screen-dialog-top-spacing-unit-multiplier 保持一致）
      const DEFAULT_TOP_SPACING_UNIT_MULTIPLIER = 120;
      if (typeof window === 'undefined' || !window.document) {
        return DEFAULT_TOP_SPACING_UNIT_MULTIPLIER; // SSR 降级方案，使用默认值
      }
      // 从 CSS 变量中读取顶部间距倍数
      const multiplierVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--wphk-design-half-screen-dialog-top-spacing-unit-multiplier');
      if (multiplierVar) {
        const multiplier = parseFloat(multiplierVar.trim());
        if (!Number.isNaN(multiplier)) {
          return multiplier;
        }
      }
      // 降级方案：使用默认值
      return DEFAULT_TOP_SPACING_UNIT_MULTIPLIER;
    };

    // 获取 @unit 的值（从 CSS 变量中读取，避免硬编码）
    const getUnitValue = (): number => {
      // @unit 的默认值（与 Less 中的 @unit: 0.005rem 保持一致）
      const DEFAULT_UNIT_VALUE = 0.005;

      if (typeof window === 'undefined' || !window.document) {
        return DEFAULT_UNIT_VALUE; // SSR 降级方案，使用默认值
      }
      // 尝试从 CSS 变量中读取 @unit 值
      const unitVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--wphk-design-half-screen-dialog-unit');
      if (unitVar) {
        // 解析 rem 值，例如 "0.005rem" -> 0.005
        const remMatch = unitVar.trim().match(/^([\d.]+)rem$/);
        if (remMatch) {
          return parseFloat(remMatch[1]);
        }
      }
      // 如果 CSS 变量不存在，尝试从已知的 CSS 变量计算
      // 例如从 --wphk-design-half-screen-dialog-top-spacing 计算
      const topSpacingUnitMultiplier = getTopSpacingUnitMultiplier();
      const topSpacingVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--wphk-design-half-screen-dialog-top-spacing');
      if (topSpacingVar) {
        const remMatch = topSpacingVar.trim().match(/^([\d.]+)rem$/);
        if (remMatch) {
          // top-spacing = topSpacingUnitMultiplier * @unit，所以 @unit = top-spacing / topSpacingUnitMultiplier
          return parseFloat(remMatch[1]) / topSpacingUnitMultiplier;
        }
      }
      // 降级方案：使用默认值
      return DEFAULT_UNIT_VALUE;
    };

    // 计算最大高度的函数
    const calculateMaxHeight = (): string => {
      if (typeof window !== 'undefined' && window.document) {
        const unitValue = getUnitValue();
        const topSpacingUnitMultiplier = getTopSpacingUnitMultiplier();
        const topSpacingRem = topSpacingUnitMultiplier * unitValue; // topSpacingUnitMultiplier*@unit
        // 获取视口高度并转换为 rem
        const viewportHeight = document.documentElement.clientHeight;
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const viewportHeightRem = viewportHeight / rootFontSize;
        // 减去顶部间距（topSpacingUnitMultiplier*@unit）
        return `${viewportHeightRem - topSpacingRem}rem`;
      }
      return '80vh'; // SSR 降级方案
    };

    // 最大高度（使用 ref 以便手动触发更新）
    const maxHeight = ref(calculateMaxHeight());

    // 弹窗样式
    const dialogStyle = computed(() => ({
      '--half-screen-dialog-max-height': maxHeight.value,
    }));

    /**
     * 检查是否有可滚动内容
     */
    const checkScrollableContent = () => {
      if (!props.scrollable || !bodyRef.value) {
        hasScrollableContent.value = false;
        return;
      }
      const body = bodyRef.value;
      hasScrollableContent.value = body.scrollHeight > body.clientHeight;
    };

    // 是否显示渐变遮罩（scrollable=false 时始终不显示）
    const shouldShowGradient = computed(() => props.scrollable
             && props.showFooterGradient
             && hasScrollableContent.value
             && !isScrolledToBottom.value);

    // 是否有底部区域（在模板中直接判断）

    /**
     * 处理滚动事件 - H5 环境
     * @param event - 原生滚动事件
     */
    const handleScroll = (event: Event) => {
      if (!props.scrollable) return;
      const target = event.target as HTMLElement;
      const threshold = 10; // 10px 阈值
      isScrolledToBottom.value = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

      // 更新可滚动内容状态
      checkScrollableContent();
    };

    /**
     * 处理滚动事件 - Liteapp 环境
     * @param event - scroll-view 组件的滚动事件
     */
    const handleScrollViewScroll = (event: any) => {
      if (!props.scrollable) return;
      const detail = event.detail || event;
      const scrollTop = detail.scrollTop || 0;

      if (!bodyRef.value) {
        return;
      }

      // 获取 scroll-view 的滚动信息
      const scrollHeight = bodyRef.value.scrollHeight || 0;
      const clientHeight = bodyRef.value.clientHeight || 0;
      const threshold = 10; // 10px 阈值

      isScrolledToBottom.value = scrollHeight - scrollTop - clientHeight < threshold;

      // 更新可滚动内容状态
      checkScrollableContent();
    };

    // 关闭按钮点击处理
    const handleCloseClick = async () => {
      await handleClose('close');
    };

    // 遮罩层点击处理
    const handleOverlayClick = async (event: Event) => {
      // 总是触发 clickOverlay 事件
      emit('clickOverlay', event);

      // 检查是否允许点击遮罩层关闭
      if (!props.closeOnClickOverlay) {
        return;
      }

      await handleClose('overlay');
    };

    // 关闭处理
    const handleClose = async (action: 'close' | 'overlay') => {
      // 执行 beforeClose 验证
      if (props.beforeClose) {
        const shouldClose = await props.beforeClose(action);
        if (!shouldClose) {
          // 验证不通过，阻止关闭
          return;
        }
      }

      // 验证通过，触发 update:visible 事件（更新外部 visible 状态）
      emit('update:visible', false);

      // 触发 close 事件（通知外部弹窗即将关闭）
      emit('close');
    };

    // 暴露给外部的方法
    const open = () => {
      emit('update:visible', true);
    };

    const close = async () => {
      await handleClose('close');
    };

    // 按钮点击处理
    const handleActionClick = (action: HalfScreenDialogAction, index: number) => {
      // 触发 action-click 事件
      emit('action-click', action, index);

      // 如果 action 定义了 onClick 回调，同时执行回调
      if (action.onClick) {
        action.onClick(action);
      }
    };

    // Popup 事件处理
    const handleOpen = () => {
      emit('open');
    };

    // Popup 的 close 事件处理（当 Popup 内部触发关闭时）
    const handleCloseEvent = () => {
      // Popup 内部可能会触发 close 事件，但我们已经在 handleClose 中处理了
      // 这里不需要重复处理，因为 close 事件会在 handleClose 中触发
    };

    const handleOpened = (el: Element) => {
      emit('opened', el);
    };

    const handleClosed = (el: Element) => {
      // 如果开启了 destroyOnClose，关闭时重置渲染状态
      if (props.destroyOnClose) {
        nextTick(() => {
          isRendered.value = false;
        });
      }
      emit('closed', el);
    };

    // 监听 visible 变化，更新渲染状态和最大高度
    watch(
      () => props.visible,
      (val) => {
        if (val && props.lazyRender) {
          isRendered.value = true;
        }
        // 当 visible 变为 true 时，重新计算最大高度（避免微信浏览器底部导航条影响）
        if (val) {
          nextTick(() => {
            maxHeight.value = calculateMaxHeight();
            // 检查是否有可滚动内容（参考 tabs.vue 的 onMounted）
            checkScrollableContent();
          });
        }
      },
    );

    return {
      n,
      classes,
      titleId,
      contentId,
      shouldRender,
      dialogStyle,
      bodyRef,
      shouldShowGradient,
      hasTitle,
      hasHeaderLeft,
      hasHeaderRight,
      isEmptyHeader,
      handleScroll,
      handleScrollViewScroll,
      handleCloseClick,
      handleOverlayClick,
      handleActionClick,
      handleOpen,
      handleCloseEvent,
      handleOpened,
      handleClosed,
      currentVisible,
      isLiteapp,
      hasScrollableContent,
      // 暴露给外部的方法
      open,
      close,
    };
  },
});
</script>
